import React, { useState, useEffect, memo, useMemo } from 'react';
import Loading from 'components/utility/Loading';
import Filters from 'components/utility/Filters';
import { ScrollView, Stack, useTheme, SizableText } from 'tamagui';
import { FlatList } from 'react-native';
import { Flex, Center } from 'components/base/General';
import { formatDefaultDate } from 'utils/misc';
import { getLevels } from 'controllers/SkillController';
import { useGetCollectionsByCategoryQuery, useGetCollectionsByTaxonQuery, useGetCategoriesQuery } from 'utils/RTKGraphQL';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';
import loadable from '@loadable/component';
import Breadcrumb from 'components/utility/Breadcrumb';
import CardRow from 'components/cards/CardRow';
const useFilteredCategories = taxonId => {
  const {
    data: allCategories,
    isLoading,
    error
  } = useGetCategoriesQuery(taxonId);
  // Updated to use skillsettings slice
  const selectedCategories = useSelector(state => state.skillsettings.cats);
  const filteredCategories = useMemo(() => {
    if (isLoading || error || !allCategories) return [];
    if (selectedCategories.length === 0) {
      return allCategories.map(category => category.id);
    }
    return allCategories.filter(category => selectedCategories.includes(category.id)).map(category => category.id);
  }, [allCategories, selectedCategories, isLoading, error]);
  return {
    filteredCategories,
    isLoading,
    error
  };
};
export const CategoryArticlesWrapper = ({
  initialData,
  taxonId,
  isBot
}) => {
  const theme = useTheme();
  const {
    filteredCategories,
    isLoading,
    error
  } = useFilteredCategories(taxonId);
  if (isBot) {
    return /*#__PURE__*/React.createElement(MultiCategoryArticle, {
      initialData: initialData
    });
  }
  if (isLoading) {
    return /*#__PURE__*/React.createElement(Flex, {
      flexGrow: 1,
      bg: theme.background,
      alignSelf: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: 500
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  return /*#__PURE__*/React.createElement(ScrollView, null, filteredCategories.map(categoryId => /*#__PURE__*/React.createElement(SingleCategoryArticle, {
    key: categoryId,
    categoryId: categoryId,
    taxonId: taxonId
  })));
};
const MultiCategoryArticle = ({
  initialData
}) => {
  return /*#__PURE__*/React.createElement(FlatList, {
    zIndex: -1,
    data: initialData,
    renderItem: (articleGroup, index) => /*#__PURE__*/React.createElement(CardRow, {
      articleGroup: articleGroup.item,
      count: index,
      key: index,
      cardForm: "WhiteCard"
    })
  });
};
const SingleCategoryArticle = ({
  categoryId,
  taxonId
}) => {
  const theme = useTheme();
  const profile = useSelector(state => state.profile);
  const userId = profile ? profile.id : undefined;
  // Updated to use languagesettings and skillsettings slices
  const showRead = useSelector(state => state.languagesettings.showRead);
  const skills = useSelector(state => state.skillsettings.skill);
  const date = useSelector(state => state.date);
  const hsks = getLevels(skills);
  const {
    data,
    isLoading,
    error
  } = useGetCollectionsByCategoryQuery({
    categoryIds: [categoryId],
    limit: 15,
    ordering: '-date',
    published: true,
    displaySkill: hsks,
    taxonId: taxonId,
    showRead: showRead,
    fillToLimit: true,
    start: formatDefaultDate(date.start_date),
    end: formatDefaultDate(date.end_date),
    userId: userId
  });
  if (isLoading) {
    return /*#__PURE__*/React.createElement(Flex, {
      flexGrow: 1,
      bg: theme.background,
      alignSelf: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: 500
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  if (error || !data) {
    return null; // or some error UI
  }
  return /*#__PURE__*/React.createElement(FlatList, {
    zIndex: -1,
    data: data,
    renderItem: (articleGroup, index) => /*#__PURE__*/React.createElement(CardRow, {
      articleGroup: articleGroup.item,
      count: index,
      key: index,
      cardForm: "WhiteCard"
    })
  });
};
const CategoryArticlesChild = ({
  initialData,
  taxonId,
  isBot
}) => {
  const theme = useTheme();
  const profile = useSelector(state => state.profile);
  const userId = profile ? profile.id : undefined;
  // Updated to use languagesettings and skillsettings slices
  const categories = useSelector(state => state.skillsettings.cats);
  const showRead = useSelector(state => state.languagesettings.showRead);
  const skills = useSelector(state => state.skillsettings.skill);
  const date = useSelector(state => state.date);
  const hsks = getLevels(skills);
  const [isClientSide, setIsClientSide] = useState(false);
  useEffect(() => {
    setIsClientSide(true);
  }, []);
  const {
    data,
    isLoading,
    error
  } = useGetCollectionsByCategoryQuery(isBot ? skipToken : {
    categoryIds: categories,
    limit: 15,
    ordering: '-date',
    published: true,
    displaySkill: hsks,
    taxonId: taxonId,
    showRead: showRead,
    fillToLimit: true,
    start: formatDefaultDate(date.start_date),
    end: formatDefaultDate(date.end_date),
    userId: userId
  });
  if (isBot) {
    return /*#__PURE__*/React.createElement(FlatList, {
      zIndex: -1,
      data: initialData,
      renderItem: (articleGroup, index) => /*#__PURE__*/React.createElement(CardRow, {
        articleGroup: articleGroup.item,
        count: index,
        key: index,
        cardForm: "WhiteCard"
      })
    });
  }
  if (!isClientSide || isLoading) {
    return /*#__PURE__*/React.createElement(Flex, {
      flexGrow: 1,
      bg: theme.background,
      alignSelf: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: 500
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  return /*#__PURE__*/React.createElement(FlatList, {
    zIndex: -1,
    data: data,
    renderItem: (articleGroup, index) => /*#__PURE__*/React.createElement(CardRow, {
      articleGroup: articleGroup.item,
      count: index,
      key: index,
      cardForm: "WhiteCard"
    })
  });
};
export const CategoryArticles = /*#__PURE__*/memo(CategoryArticlesChild);
export const RecentArticles = /*#__PURE__*/React.memo(({
  initialData,
  taxonId,
  mainPage,
  title,
  url,
  isBot
}) => {
  const theme = useTheme();
  const profile = useSelector(state => state.profile);
  const userId = profile ? profile.id : undefined;
  // Updated to use languagesettings and skillsettings slices
  const showRead = useSelector(state => state.languagesettings.showRead);
  const skills = useSelector(state => state.skillsettings.skill);
  const hsks = getLevels(skills);
  const [isClientSide, setIsClientSide] = useState(false);
  useEffect(() => {
    setIsClientSide(true);
  }, []);
  const {
    data,
    isLoading,
    error
  } = useGetCollectionsByTaxonQuery(isBot ? skipToken : {
    limit: 15,
    ordering: '-date',
    published: true,
    displaySkill: hsks,
    showRead: showRead,
    fillToLimit: true,
    taxonId: taxonId,
    userId: userId
  });
  if (isBot) {
    return /*#__PURE__*/React.createElement(FlatList, {
      zIndex: -1,
      data: [initialData],
      renderItem: ({
        item: articleGroup,
        index
      }) => /*#__PURE__*/React.createElement(CardRow, {
        articleGroup: articleGroup,
        overrideCategory: !mainPage ? title : null,
        count: index,
        key: index,
        cardForm: "WhiteCard",
        url: url
      })
    });
  }
  if (!isClientSide || isLoading) {
    return /*#__PURE__*/React.createElement(Flex, {
      flexGrow: 1,
      bg: theme.background,
      alignSelf: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: 500
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  return /*#__PURE__*/React.createElement(FlatList, {
    zIndex: -1,
    data: [data],
    renderItem: ({
      item: articleGroup,
      index
    }) => /*#__PURE__*/React.createElement(CardRow, {
      articleGroup: articleGroup,
      overrideCategory: !mainPage ? title : null,
      count: index,
      key: index,
      cardForm: "WhiteCard",
      url: url
    })
  });
});